import { useEffect, useRef } from 'react'
import type { CSSProperties, RefObject } from 'react'
import type { CanvasSize } from '../../lib/simulation-core'

interface SimulationCanvasStageProps {
  canvasRef?: RefObject<HTMLCanvasElement | null>
  onResize?: (size: CanvasSize) => void
  className?: string
  style?: CSSProperties
  aspectRatio?: string
  ariaLabel?: string
}

export function SimulationCanvasStage({
  canvasRef,
  onResize,
  className,
  style,
  aspectRatio = '3 / 2',
  ariaLabel = 'Simulation canvas',
}: SimulationCanvasStageProps) {
  const fallbackRef = useRef<HTMLCanvasElement>(null)
  const targetRef = canvasRef ?? fallbackRef

  useEffect(() => {
    const canvas = targetRef.current
    if (!canvas || !onResize) return

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      const width = Math.max(1, Math.round(rect.width * dpr))
      const height = Math.max(1, Math.round(rect.height * dpr))

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
      }

      onResize({ width, height, dpr })
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)

    return () => observer.disconnect()
  }, [onResize, targetRef])

  return (
    <div
      className={['simulation-canvas-stage', className].filter(Boolean).join(' ')}
      style={{
        ...style,
      }}
    >
      <canvas
        ref={targetRef}
        aria-label={ariaLabel}
        style={{ aspectRatio }}
      />
    </div>
  )
}
